import React, { useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, Divider, Stack, TextField, Button,
  InputAdornment, IconButton, Avatar, Snackbar, Alert, CircularProgress, Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import { BASE_URL, getBearerToken, putBearerToken } from '../../utils/Apis';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Constants } from '../../utils/Constants';
import AppShell from '../../layouts/AppShell';

// Admin's own profile. Data flow preserved: GET users, PUT users
// (first_name/phone/messages[/password]), avatar via PUT users_image_update.
const A_profile = () => {
  const location = useLocation();

  const [firstName, setfirstName] = useState('');
  const [phone, set_phone] = useState('');
  const [from_date, set_from_date] = useState('');
  const [to_date, set_to_date] = useState('');
  const [slug_name, set_slug_name] = useState('');
  const [password, set_password] = useState('');
  const [old_password, set_old_password] = useState('');
  // Hosts (SubAdmin) must give their current password to change it (server rule).
  const isHost = (localStorage.getItem(Constants.USERTYPE) || '') === 'SubAdmin';
  const [confirm_password, set_confirm_password] = useState('');
  const [hostmessage, sethostmessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [file, setfile] = useState('');

  const [loader, setloader] = useState(false);
  const [fetchLoader, setfetchLoader] = useState(false);
  const [rendered, setrendered] = useState(0);
  const [toast, setToast] = useState(null);
  const notify = (msg, severity = 'info') => setToast({ msg, severity });

  React.useEffect(() => { fetch_users(); /* eslint-disable-next-line */ }, [rendered]);

  const fetch_users = async () => {
    setfetchLoader(true);
    getBearerToken('users').then((result) => {
      if (result.error === 0) {
        const d = result.data;
        setfirstName(d.first_name || '');
        set_phone(d.phone || '');
        set_from_date(d.from_date ? d.from_date.split('T')[0] : '');
        set_to_date(d.to_date ? d.to_date.split('T')[0] : '');
        set_slug_name(d.slug_name || '');
        setfile(d.image || '');
        sethostmessage(d.messages || '');
      }
      setfetchLoader(false);
    });
  };

  const submit = async () => {
    setloader(true);
    let json;
    if (password !== '') {
      json = { first_name: firstName, phone, password, show_password: password };
      if (isHost) {
        if (old_password === '') { notify('Enter your current password to change it.', 'error'); setloader(false); return; }
        json.old_password = old_password;
      }
    } else {
      json = { first_name: firstName, phone };
    }
    if (!isHost) json.messages = hostmessage;
    if (phone === '') { notify('Phone is required.', 'error'); setloader(false); return; }
    if (confirm_password !== password) { notify('Passwords do not match.', 'error'); setloader(false); return; }
    putBearerToken('users', json).then((result) => {
      if (result.error === 0) notify('Profile saved.', 'success');
      else notify(result.status || result.message || 'Something went wrong.', 'error');
      setloader(false);
    });
  };

  const upload_update_docs = async (e) => {
    const formData = new FormData();
    const token = localStorage.getItem(Constants.APP_KEY);
    formData.append('file', e.target.files[0]);
    try {
      await axios({
        method: 'PUT', url: BASE_URL + 'users_image_update',
        headers: { 'Content-Type': 'multipart/form-data', Authorization: 'Bearer ' + token },
        data: formData,
      });
      setrendered((o) => o + 1);
      notify('Photo updated.', 'success');
    } catch (error) { notify('Photo upload failed.', 'error'); }
  };

  return (
    <AppShell title="Profile" breadcrumb={['Profile']}>
      <Box sx={{ maxWidth: 720 }}>
        <Card>
          <CardHeader title="Your profile" subheader="Update your name, contact, broadcast message and photo." titleTypographyProps={{ variant: 'h5' }} />
          <Divider />
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {fetchLoader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={3}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Avatar src={file ? BASE_URL + file : undefined} sx={{ width: 76, height: 76, fontSize: 28, bgcolor: 'primary.main' }}>
                    {(firstName || 'A').charAt(0).toUpperCase()}
                  </Avatar>
                  <Button component="label" variant="outlined" startIcon={<PhotoCameraRoundedIcon />}>
                    Change photo
                    <input hidden type="file" accept="image/*" onChange={upload_update_docs} />
                  </Button>
                </Stack>

                <TextField label="Full Name" value={firstName} onChange={(e) => setfirstName(e.target.value)} />
                <TextField label="Mobile Number" value={phone} onChange={(e) => set_phone(e.target.value)} />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Start Date" value={from_date} InputProps={{ readOnly: true }} />
                  <TextField label="Expiry Date" value={to_date} InputProps={{ readOnly: true }} />
                </Stack>

                <TextField label="Username" value={slug_name} InputProps={{ readOnly: true }} helperText="Your login username" />

                {!isHost && (
                  <TextField label="Broadcast Message" value={hostmessage} onChange={(e) => sethostmessage(e.target.value)}
                    helperText="Shown to your participants" multiline minRows={2} />
                )}

                <Divider textAlign="left"><Typography variant="subtitle2" color="text.secondary">Change password</Typography></Divider>

                {isHost && (
                  <TextField label="Current Password" type={showPassword ? 'text' : 'password'}
                    inputProps={{ autoComplete: 'current-password', name: 'profile-current-password' }}
                    value={old_password} onChange={(e) => set_old_password(e.target.value)}
                    helperText="Needed only when changing your password" />
                )}

                <TextField
                  label="New Password" type={showPassword ? 'text' : 'password'}
                  inputProps={{ autoComplete: 'new-password', name: 'profile-new-password' }}
                  value={password} onChange={(e) => set_password(e.target.value)}
                  helperText="Leave blank to keep your current password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField label="Confirm Password" type={showPassword ? 'text' : 'password'}
                  inputProps={{ autoComplete: 'new-password' }}
                  value={confirm_password} onChange={(e) => set_confirm_password(e.target.value)} />

                <Divider />
                <Stack direction="row" justifyContent="flex-end">
                  <Button variant="contained" onClick={submit} disabled={loader}
                    startIcon={loader ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}>
                    {loader ? 'Saving…' : 'Save changes'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </AppShell>
  );
};

export default A_profile;
